import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileText, X, ChevronDown, Check, ArrowRight, Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const FORMATS = ["PDF", "DOCX", "XLSX", "PPTX", "JPG/PNG"];
const inputCls =
  "w-full h-11 px-3.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#C87A4B]/40 focus:border-[#C87A4B] transition-all duration-200";

function Field({ label, optional, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">
        {label} {optional && <span className="normal-case tracking-normal text-slate-400">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

export function QuoteForm() {
  const [config, setConfig] = useState({ languages: [], industries: [], services: [] });
  const [source, setSource] = useState("English");
  const [targets, setTargets] = useState([]);
  const [wordCount, setWordCount] = useState("");
  const [industry, setIndustry] = useState("General");
  const [service, setService] = useState("Professional Translation");
  const [delivery, setDelivery] = useState("Standard");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState("form");
  const [progress, setProgress] = useState({});
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    api.get("/languages").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const langNames = config.languages.map((l) => l.name);

  const toggleTarget = (lang) =>
    setTargets((t) => (t.includes(lang) ? t.filter((x) => x !== lang) : [...t, lang]));

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (incoming.length) setFiles((f) => [...f, ...incoming]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!source || targets.length === 0) return toast.error("Please choose your source and target languages.");
    if (!name.trim() || !email.trim()) return toast.error("Please add your name and email.");
    setPhase("submitting");
    try {
      const { data: quote } = await api.post("/quotes", {
        name, email, company, notes,
        source_language: source,
        target_languages: targets,
        word_count: wordCount ? parseInt(wordCount, 10) : null,
        industry, service_type: service, delivery,
      });
      setResult(quote);
      if (files.length > 0) {
        setPhase("uploading");
        for (let i = 0; i < files.length; i++) {
          const fd = new FormData();
          fd.append("quote_ref", quote.quote_ref);
          fd.append("file", files[i]);
          await api.post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (ev) =>
              setProgress((p) => ({ ...p, [i]: Math.round((ev.loaded * 100) / (ev.total || 1)) })),
          });
        }
      }
      setPhase("done");
      toast.success("Quote request submitted.");
    } catch (err) {
      setPhase("form");
      toast.error(formatApiError(err));
    }
  };

  if (phase === "done" && result) {
    return (
      <motion.div
        data-testid="quote-confirmation"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(11,17,30,0.08)] p-8 sm:p-10"
      >
        <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6">
          <Check className="w-6 h-6" />
        </div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Quote Reference</p>
        <div className="flex items-center gap-3 mb-6">
          <span data-testid="quote-reference" className="font-heading text-3xl font-bold text-[#0B111E]">
            {result.quote_ref}
          </span>
          <button
            data-testid="quote-copy-ref-btn"
            onClick={() => { navigator.clipboard?.writeText(result.quote_ref); toast.success("Reference copied"); }}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#C87A4B] hover:border-[#C87A4B] transition-colors"
            aria-label="Copy reference"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {result.quote_type === "instant" ? (
          <div data-testid="quote-instant-result" className="rounded-xl bg-[#FAF8F5] border border-slate-200 p-6 mb-6">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-4">Indicative Estimate</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-slate-500">Translation</p><p className="font-semibold text-[#0B111E]">{result.source_language} → {result.target_languages.join(", ")}</p></div>
              <div><p className="text-slate-500">Word count</p><p className="font-semibold text-[#0B111E]">{result.word_count?.toLocaleString() || "—"}</p></div>
              <div><p className="text-slate-500">Estimated delivery</p><p className="font-semibold text-[#0B111E]">{result.estimated_delivery}</p></div>
              <div><p className="text-slate-500">Estimate</p><p data-testid="quote-price-estimate" className="font-heading text-2xl font-bold text-[#C87A4B]">£{result.price_estimate?.toFixed(2)}</p></div>
            </div>
            <p className="text-xs text-slate-400 mt-4">Indicative price. Our team will confirm your final quotation by email.</p>
          </div>
        ) : (
          <div data-testid="quote-manual-result" className="rounded-xl bg-[#FAF8F5] border border-slate-200 p-6 mb-6">
            <p className="text-sm text-slate-600 leading-relaxed">
              Thank you. Our team will review your documents and send you a quotation shortly.
            </p>
          </div>
        )}

        {files.length > 0 && (
          <p className="text-xs text-slate-500 mb-6 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            {files.length} document{files.length > 1 ? "s" : ""} securely uploaded and linked to your enquiry.
          </p>
        )}

        <button
          data-testid="quote-new-request-btn"
          onClick={() => { setPhase("form"); setResult(null); setFiles([]); setProgress({}); setTargets([]); setWordCount(""); setNotes(""); }}
          className="text-sm font-semibold text-[#C87A4B] hover:text-[#B3673A] transition-colors"
        >
          Submit another request →
        </button>
      </motion.div>
    );
  }

  const busy = phase === "submitting" || phase === "uploading";

  return (
    <motion.form
      onSubmit={submit}
      data-testid="quote-form"
      initial={{ opacity: 0, y: 32, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(11,17,30,0.10)] p-6 sm:p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Instant Quotation</p>
        <span className="text-xs font-mono text-[#C87A4B]">TR — Engine</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="From" testId="quote-field-from">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="quote-source-language" className="h-11 rounded-lg">
              <SelectValue placeholder="Select source language" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {langNames.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="To" testId="quote-field-to">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="quote-target-languages"
                className="w-full h-11 px-3.5 rounded-lg border border-slate-200 bg-white text-sm flex items-center justify-between hover:border-[#C87A4B] transition-colors"
              >
                <span className={targets.length ? "text-slate-900 truncate" : "text-slate-400"}>
                  {targets.length ? targets.join(", ") : "Select target language(s)"}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2 max-h-64 overflow-y-auto" align="start">
              {langNames.filter((l) => l !== source).map((l) => (
                <button
                  type="button"
                  key={l}
                  data-testid={`quote-target-option-${l.toLowerCase().replace(/[\s()]+/g, "-")}`}
                  onClick={() => toggleTarget(l)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm hover:bg-slate-50 transition-colors"
                >
                  <span>{l}</span>
                  {targets.includes(l) && <Check className="w-4 h-4 text-[#C87A4B]" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          {targets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {targets.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs bg-[#0B111E] text-white rounded-full px-2.5 py-1">
                  {t}
                  <button type="button" onClick={() => toggleTarget(t)} aria-label={`Remove ${t}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Upload Document" optional testId="quote-field-upload">
          <div
            data-testid="quote-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileInput.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition-all duration-200 ${
              dragging ? "border-[#C87A4B] bg-[#C87A4B]/5" : "border-slate-200 hover:border-[#C87A4B]/60 hover:bg-slate-50"
            }`}
          >
            <UploadCloud className="w-7 h-7 mx-auto text-[#C87A4B] mb-2" strokeWidth={1.6} />
            <p className="text-sm font-medium text-slate-700">Drag &amp; drop documents</p>
            <p className="text-xs text-slate-400 mt-0.5">or <span className="text-[#C87A4B] font-semibold">browse files</span></p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              {FORMATS.map((f) => (
                <span key={f} className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{f}</span>
              ))}
            </div>
            <input
              ref={fileInput}
              type="file"
              multiple
              data-testid="quote-file-input"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.txt,.csv"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
          <AnimatePresence>
            {files.map((f, i) => (
              <motion.div
                key={`${f.name}-${i}`}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2.5 mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <FileText className="w-4 h-4 text-[#C87A4B] shrink-0" />
                <span className="text-xs text-slate-700 truncate flex-1">{f.name}</span>
                {phase === "uploading" ? (
                  <span className="text-[10px] font-mono text-[#C87A4B]">{progress[i] || 0}%</span>
                ) : (
                  <button type="button" onClick={() => setFiles((fl) => fl.filter((_, x) => x !== i))} aria-label="Remove file">
                    <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <Field label="Word Count" optional testId="quote-field-words">
          <input
            type="number" min="0" value={wordCount}
            onChange={(e) => setWordCount(e.target.value)}
            data-testid="quote-word-count"
            placeholder="e.g. 2,500"
            className={inputCls}
          />
        </Field>
        <Field label="Subject / Industry" testId="quote-field-industry">
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger data-testid="quote-industry" className="h-11 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {config.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Service Type" testId="quote-field-service">
          <Select value={service} onValueChange={setService}>
            <SelectTrigger data-testid="quote-service" className="h-11 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {config.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Delivery" testId="quote-field-delivery">
          <div className="grid grid-cols-2 gap-2">
            {["Standard", "Urgent"].map((d) => (
              <button
                type="button"
                key={d}
                data-testid={`quote-delivery-${d.toLowerCase()}`}
                onClick={() => setDelivery(d)}
                className={`h-11 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                  delivery === d
                    ? "border-[#C87A4B] bg-[#C87A4B]/10 text-[#B3673A]"
                    : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="border-t border-slate-100 mt-6 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name" testId="quote-field-name">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="quote-name" placeholder="Your full name" className={inputCls} />
        </Field>
        <Field label="Company" optional testId="quote-field-company">
          <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="quote-company" placeholder="Company name" className={inputCls} />
        </Field>
        <Field label="Email" testId="quote-field-email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quote-email" placeholder="you@company.com" className={inputCls} />
        </Field>
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="quote-submit"
        className="mt-6 w-full h-13 py-4 rounded-lg bg-[#C87A4B] hover:bg-[#B3673A] disabled:opacity-60 text-white font-heading font-bold text-lg tracking-tight flex items-center justify-center gap-2 shadow-lg shadow-[#C87A4B]/25 transition-all duration-200 hover:-translate-y-0.5"
      >
        {busy ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {phase === "uploading" ? "Uploading documents…" : "Preparing your quote…"}
          </>
        ) : (
          <>Get My Quote <ArrowRight className="w-5 h-5" /></>
        )}
      </button>
      <p className="text-[11px] text-slate-400 text-center mt-3 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Secure &amp; confidential. No payment required at this stage.
      </p>
    </motion.form>
  );
}
